import { useCallback, SyntheticEvent, useContext, useEffect } from 'react';
import { Location, useLocation } from 'react-router-dom';

import { trackGameInfoPlayClick, trackGameInfoOpen, trackGameInfoClosed } from '@comeon/analytics';

import useGameLauncher from '~/components/casino-shared/explore/utilities/use-game-launcher';
import useCasinoLocation from '~/hooks/use-casino-location.js';
import useTranslations from '~/components/translation/use-translations';
import ModalDesktop from '~/components/modal/modal';
import useGetDimensions from '~/hooks/use-get-dimensions';
import { sizes } from '~/utils/style-utils';
import useToggleGameInfo from '~/components/casino-shared/explore/utilities/use-toggle-game-info';
import UIContext from '~/ui/ui-context';

import ModalMobile from './modal-mobile';
import PlayButton from './play-button';
import PrimarySection from './primary-section';
import SecondarySection from './secondary-section';
import useGameInfoReducer from './use-game-info';
import { GameInfoWrapper, GameInfoContainer, DESKTOP_MODAL_HEIGHT, DESKTOP_TOTAL_WIDTH } from './game-info.styles';

const textKeys = [
	'LOVEGAMES_LOGOUT',
	'MOBILE_TRUSTLY_DEPOSIT_LOGIN',
	'PRACTICE_MODE',
	'PLAY',
	'PLAY_4_REAL',
	'ADD_TO_FAVORITES',
	'MOBILE_LOGIN_LOGIN_BUTTON'
];

const GameInfo = () => {
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);
	const { hasStaticHeader } = useContext(UIContext);

	const { width, height } = useGetDimensions();
	const { toggleGameInfo } = useToggleGameInfo();

	const { gameInfo, game } = useGameInfoReducer({ isLoadingTranslations });
	const { gameLauncher } = useGameLauncher();

	const { isLiveCasinoLocation } = useCasinoLocation();
	const location = useLocation() as Location<{ gameCode: string; listName: string; listGameIndex: string }>;
	const gameCode = location?.state?.gameCode;
	const listName = location?.state?.listName;
	const listGameIndex = location?.state?.listGameIndex;

	useEffect(() => {
		if (!game) {
			return;
		}

		trackGameInfoOpen({
			game,
			category: isLiveCasinoLocation ? 'Live Casino' : 'Casino'
		});
	}, [game, isLiveCasinoLocation]);

	const playGame = useCallback(
		({ event, mode, depositLogin }: { mode: string; event: SyntheticEvent; depositLogin?: boolean }) => {
			if (!game) {
				return;
			}
			trackGameInfoPlayClick({
				game,
				mode,
				category: isLiveCasinoLocation ? 'Live Casino' : 'Casino'
			});
			gameLauncher({
				event,
				mode,
				game,
				depositLogin,
				listName,
				listGameIndex
			});
		},
		[game, gameLauncher, listGameIndex, listName, isLiveCasinoLocation]
	);

	const closeGameInfo = useCallback(() => {
		if (!game) {
			return;
		}
		trackGameInfoClosed({ game, category: isLiveCasinoLocation ? 'Live Casino' : 'Casino' });
		toggleGameInfo(gameCode, listName, listGameIndex);
	}, [game, gameCode, listGameIndex, listName, toggleGameInfo, isLiveCasinoLocation]);

	const handleOutsideClick = (e: React.MouseEvent<HTMLElement>) => {
		if (e.target instanceof HTMLElement && !e.target.closest('#game-info-modal')) {
			closeGameInfo();
		}
	};

	const desktop = width >= sizes.lg && height >= DESKTOP_MODAL_HEIGHT;

	const Modal = desktop ? ModalDesktop : ModalMobile;

	if (isLoadingTranslations || !game) {
		return null;
	}

	return (
		<Modal
			show={!!gameCode}
			blurBg
			screenCenter
			modalHeight={desktop ? DESKTOP_MODAL_HEIGHT : undefined}
			modalWidth={DESKTOP_TOTAL_WIDTH}
			bodyScrollDisabled
			small={!desktop}
			onClick={handleOutsideClick}
		>
			<GameInfoWrapper
				id="game-info-modal"
				data-at={gameCode ? 'game-support-active' : undefined}
				hasStaticHeader={hasStaticHeader}
			>
				<GameInfoContainer>
					<PrimarySection
						playGame={playGame}
						t={t}
						desktop={desktop}
						closeGameInfo={closeGameInfo}
						seats={gameInfo.seats}
						lastWinningNumbers={gameInfo.lastWinningNumbers}
						game={game}
					/>
					<SecondarySection gameInfo={gameInfo} categoryCode={game.categoryCode} game={game} />
					{!desktop && <PlayButton playGame={playGame} t={t} />}
				</GameInfoContainer>
			</GameInfoWrapper>
		</Modal>
	);
};

export default GameInfo;
